import { faUser } from "@fullscript/pro-solid-svg-icons";
import { useTranslation } from "react-i18next";

import { Pill } from "@aviary/components/Pill";
import { l } from "@aviary/locales/i18n";
import { FontAwesomeIcon } from "@shared/react-fontawesome/react-fontawesome";

import type { AvatarProps } from "./Avatar.types";
import { PlatformBadge } from "./PlatformBadge/PlatformBadge";

import * as styles from "./Avatar.styles";

const Avatar = ({
  size = "medium",
  isWellevate = false,
  isRupa = false,
  overlayPosition = "bottomLeft",
  isTopNav = false,
  ...props
}: AvatarProps) => {
  const { t } = useTranslation();

  const {
    image,
    firstName,
    lastName,
    label,
    // isLight only is used for DS3 version, so we're extracting it out
    // here to ensure no DOM errors
    // oxlint-disable-next-line @typescript-eslint/no-unused-vars
    isLight,
    userStatus,
    children,
    onLoadImageError,
    ...rest
  } = props;

  const wrapperStyles = [
    styles.avatar,
    size && styles.sizes[size],
    userStatus && !image && styles.avatarWithStatus,
    isTopNav && styles.topNavSize,
  ];

  const overlayChipStyles = [
    styles.overlayChip,
    overlayPosition && styles.overlayPosition[overlayPosition](size, isWellevate || isRupa),
  ];

  const imageStyles = [styles.image, userStatus && styles.avatarWithStatus];

  const canRenderName = firstName && lastName && firstName.length > 0 && lastName.length > 0;

  const getNonAvatarContents = () => {
    if (canRenderName) {
      const initials = firstName[0] + lastName[0];

      return initials.toUpperCase();
    }

    return <FontAwesomeIcon icon={faUser} />;
  };

  const renderStatusPill = () => {
    if (!userStatus) return null;

    const pillSize = size === "large" || size === "xlarge" ? "medium" : "xsmall";

    if (userStatus === "archived") {
      return (
        <div css={styles.userStatusPosition}>
          <Pill isColor="warning" size={pillSize}>
            {t(l.aviary.avatar.archived)}
          </Pill>
        </div>
      );
    }

    return (
      <div css={styles.userStatusPosition}>
        <Pill isColor="danger" size={pillSize}>
          {t(l.aviary.avatar.blocked)}
        </Pill>
      </div>
    );
  };

  const renderOverlayChip = () => {
    if (children) {
      return <div css={overlayChipStyles}>{children}</div>;
    }

    if (isWellevate || isRupa) {
      return (
        <div css={overlayChipStyles}>
          <PlatformBadge size={size} platform={isRupa ? "rupa" : "wellevate"} />
        </div>
      );
    }

    return null;
  };

  const renderImageOrName = () => {
    if (image) {
      return (
        <img
          src={image}
          css={imageStyles}
          alt={label}
          aria-hidden={!label}
          onError={onLoadImageError}
        />
      );
    }

    return <span css={!!isTopNav && styles.topNavNonAvatarContents}>{getNonAvatarContents()}</span>;
  };

  return (
    <div css={wrapperStyles} {...rest}>
      {renderImageOrName()}
      {renderStatusPill()}
      {renderOverlayChip()}
    </div>
  );
};

export { Avatar, type AvatarProps };
